import * as React from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Typography,
    useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'
import CheckIcon from '@mui/icons-material/Check'

// Free values for the upload, graph and AI-chat rows are the enforced caps
// (lib/planEntitlements.js, infranodus-backend src/plans/entitlements.ts);
// the other rows are product decisions, displayed only (not enforced here).
const comparisonData = [
    {
        feature: 'Graphs',
        free: '3',
        basic: 'Unlimited',
        advanced: 'Unlimited',
        premium: 'Unlimited',
    },
    {
        feature: 'AI Chats',
        free: '3 (10 messages each)',
        basic: 'Unlimited',
        advanced: 'Unlimited',
        premium: 'Unlimited',
    },
    {
        feature: 'Full Graph Analytics',
        free: 'Limited',
        basic: 'check',
        advanced: 'check',
        premium: 'check',
    },
    {
        feature: 'Browser Extension',
        free: 'Limited',
        basic: 'check',
        advanced: 'check',
        premium: 'check',
    },
    {
        feature: 'Obsidian Plugin',
        free: 'Limited',
        basic: 'check',
        advanced: 'check',
        premium: 'check',
    },
    {
        feature: 'GPT-6.1 / Opus 5.5 / Gemini 4 access',
        free: 'Limited',
        basic: 'Limited',
        advanced: 'check',
        premium: 'check',
    },
    {
        feature: 'n8n Automation Node',
        free: 'Limited',
        basic: 'check',
        advanced: 'check',
        premium: 'check',
    },
    {
        feature: 'API Access',
        free: 'Limited',
        basic: 'Limited (unlimited on annual)',
        advanced: 'Limited (unlimited on annual)',
        premium: 'Unlimited',
    },
    {
        feature: 'MCP Server',
        free: 'Limited',
        basic: 'Limited (unlimited on annual)',
        advanced: 'Limited (unlimited on annual)',
        premium: 'Unlimited',
    },
    {
        feature: 'File Upload (TXT, CSV), per file',
        free: '500 Kb',
        basic: '1 Mb',
        advanced: '5 Mb',
        premium: '10 Mb',
    },
    {
        feature: 'PDF Upload, per file',
        free: '1 Mb',
        basic: '2 Mb',
        advanced: '5 Mb',
        premium: '50 Mb',
    },
    {
        feature: 'Batch File Upload',
        free: '1',
        basic: '2',
        advanced: '20',
        premium: '50',
    },
    {
        feature: 'Google Results',
        free: '20',
        basic: '40',
        advanced: '60',
        premium: '100',
    },
    {
        feature: 'YouTube Results',
        free: '20',
        basic: '40',
        advanced: '100',
        premium: '1000',
    },
    {
        feature: 'AI Credits / Hour',
        free: '20',
        basic: '40',
        advanced: '80',
        premium: '200',
    },
    {
        feature: 'API Requests / Week',
        free: '50',
        basic: '100',
        advanced: '500',
        premium: 'Unlimited',
    },
    {
        feature: 'Live Graph Updates',
        free: '—',
        basic: '—',
        advanced: '5',
        premium: '20',
    },
    {
        feature: 'Support Response',
        free: 'Limited',
        basic: '72 hrs',
        advanced: '48 hrs',
        premium: '24 hrs',
    },
    {
        feature: 'Commercial Use',
        free: '—',
        basic: '—',
        advanced: 'check',
        premium: 'check',
    },
    {
        feature: 'Training (monthly)',
        free: '—',
        basic: '—',
        advanced: '—',
        premium: '1 hour',
    },
]

const PLAN_COLUMNS = [
    { key: 'free', label: 'Free' },
    { key: 'basic', label: 'Basic' },
    { key: 'advanced', label: 'Advanced' },
    { key: 'premium', label: 'Premium' },
]

// The design is the one from views/components/common/pricing-comparison-modal.ejs
// — lavender header, green ticks, amber "Limited" — but taken from the theme
// rather than hardcoded, because those values were written for a white page
// and the app is usually dark: the hover painted light grey under light text.
function comparisonPalette(theme) {
    const dark = theme.palette.mode === 'dark'
    return {
        // Opaque on purpose: the header is sticky, so a translucent one lets
        // the rows scroll visibly through it.
        headerBackground: dark ? '#272441' : '#f8f7ff',
        headerText: dark ? '#b9b5ff' : '#4a47a3',
        rowHover: theme.palette.action.hover,
        included: dark ? '#4ade80' : '#22c55e',
        limited: dark ? '#fbbf24' : '#f59e0b',
        absent: theme.palette.text.disabled,
    }
}

const CellValue = ({ value }) => {
    const palette = comparisonPalette(useTheme())
    if (value === 'check') {
        return (
            <CheckIcon sx={{ color: palette.included, fontSize: '1.2rem' }} />
        )
    }
    if (value === 'Limited') {
        return (
            <Typography
                variant="body2"
                sx={{ color: palette.limited, fontSize: '0.75rem' }}
            >
                Limited
            </Typography>
        )
    }
    if (value === '—') {
        return (
            <Typography variant="body2" sx={{ color: palette.absent }}>
                —
            </Typography>
        )
    }
    return <Typography variant="body2">{value}</Typography>
}

function PricingComparisonModal({ open, onClose }) {
    const theme = useTheme()
    const palette = comparisonPalette(theme)
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
            // Above the upgrade dialog (1400) and the onboarding guide (1310),
            // both of which can open this comparison.
            sx={{ zIndex: 1450 }}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 2,
                    maxHeight: isMobile ? '100%' : '90vh',
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    py: 1.5,
                    px: 2,
                }}
            >
                <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                    Plan Comparison
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{ color: 'text.secondary' }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
                <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{ maxHeight: isMobile ? 'calc(100vh - 64px)' : '70vh' }}
                >
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sx={{
                                        fontWeight: 600,
                                        backgroundColor:
                                            palette.headerBackground,
                                        color: palette.headerText,
                                        fontSize: isMobile
                                            ? '0.75rem'
                                            : '0.85rem',
                                    }}
                                >
                                    Feature
                                </TableCell>
                                {PLAN_COLUMNS.map(({ key, label }) => (
                                    <TableCell
                                        key={key}
                                        align="center"
                                        sx={{
                                            fontWeight: 600,
                                            backgroundColor:
                                                palette.headerBackground,
                                            color: palette.headerText,
                                            fontSize: isMobile
                                                ? '0.75rem'
                                                : '0.85rem',
                                        }}
                                    >
                                        {label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {comparisonData.map((row, index) => (
                                <TableRow
                                    key={index}
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: palette.rowHover,
                                        },
                                    }}
                                >
                                    <TableCell
                                        sx={{
                                            fontWeight: 500,
                                            fontSize: isMobile
                                                ? '0.7rem'
                                                : '0.8rem',
                                            py: isMobile ? 1 : 1.5,
                                        }}
                                    >
                                        {row.feature}
                                    </TableCell>
                                    {PLAN_COLUMNS.map(({ key }) => (
                                        <TableCell
                                            key={key}
                                            align="center"
                                            sx={{
                                                fontSize: isMobile
                                                    ? '0.7rem'
                                                    : '0.8rem',
                                                py: isMobile ? 1 : 1.5,
                                            }}
                                        >
                                            <CellValue value={row[key]} />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
        </Dialog>
    )
}

export default PricingComparisonModal
